import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { VersionNavigation } from "@/components/VersionNavigation";
import { toast } from "@/hooks/use-toast";
import { CheckCircle, Trash2, RotateCcw } from "lucide-react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import { User, useUser } from "../../context/UserContext";

// Helper function to get CSRF token from meta tags
const getCsrfToken = () => {
    const csrfTokenTag = document.querySelector("meta[name='csrf-token']");
    const csrfParamTag = document.querySelector("meta[name='csrf-param']");

    if (!csrfTokenTag || !csrfParamTag) {
        console.error("CSRF token vagy param meta tag nem található!");
        return { token: null, param: null };
    }

    return {
        token: csrfTokenTag.getAttribute("content"),
        param: csrfParamTag.getAttribute("content"),
    };
};

// Helper function for pagination
const generatePaginationPages = (currentPage: number, totalPages: number, pagesToShow: number = 5): (number | string)[] => {
    const pages: (number | string)[] = [];
    const halfPagesToShow = Math.floor(pagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfPagesToShow);
    let endPage = Math.min(totalPages, currentPage + halfPagesToShow);

    if (endPage - startPage + 1 < pagesToShow) {
        if (startPage === 1) {
            endPage = Math.min(totalPages, startPage + pagesToShow - 1);
        } else if (endPage === totalPages) {
            startPage = Math.max(1, totalPages - pagesToShow + 1);
        }
    }

    if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
            pages.push("...");
        }
    }

    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
            pages.push("...");
        }
        pages.push(totalPages);
    }

    return Array.from(new Set(pages));
};

interface RussianRouletteTabProps {
    gameId?: string;
    versionId?: string;
    fileId?: string;
    user: User | null;
    mainActiveTab?: string;
}

const Gomb3Page: React.FC<RussianRouletteTabProps> = ({
    gameId: propGameId,
    versionId: propVersionId,
    fileId: _propFileId,
    user,
}) => {
    const { gameId: paramGameId, versionId: paramVersionId, fileId: _paramFileId } = useParams();
    const gameId = propGameId || paramGameId;
    const versionId = propVersionId || paramVersionId;
    // const fileId = propFileId || paramFileId; // unused
    const queryClient = useQueryClient();
    const [russianRoulettePage, setRussianRoulettePage] = useState(1);
    const [appliedRussianRouletteFileFilter, setAppliedRussianRouletteFileFilter] = useState<string[]>([]);
    const [pendingRussianRouletteFileFilter, setPendingRussianRouletteFileFilter] = useState<string[]>([]);
    const [geminiResults, setGeminiResults] = useState(new Map<number, string>());
    const [acceptingIds, setAcceptingIds] = useState(new Set<number>());
    const [acceptedIds, setAcceptedIds] = useState(new Set<number>());

    // Russian Roulette data - single query with file filter support
    const { data: russianRouletteData, isLoading: isLoadingRussianRoulette } = useQuery({
        queryKey: ["russianRoulette", gameId, versionId, russianRoulettePage, appliedRussianRouletteFileFilter.sort().join(",")],
        queryFn: async () => {
            const params = new URLSearchParams({
                page: russianRoulettePage.toString(),
                sort_by: 'length',
                sort_direction: 'asc',
            });
            if (appliedRussianRouletteFileFilter.length > 0) {
                appliedRussianRouletteFileFilter.forEach(file => params.append('files[]', file));
            }
            const response = await fetch(`/api/games/${gameId}/versions/${versionId}/russian_roulette?${params.toString()}`);
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            return response.json();
        },
        enabled: !!gameId && !!versionId,
        refetchOnWindowFocus: false,
    });

    const allRussianRouletteFiles = useMemo((): string[] => {
        return russianRouletteData?.available_files || [];
    }, [russianRouletteData?.available_files]);

    useEffect(() => {
        if (allRussianRouletteFiles.length > 0 && pendingRussianRouletteFileFilter.length === 0) {
            setPendingRussianRouletteFileFilter([]);
            setAppliedRussianRouletteFileFilter([...allRussianRouletteFiles]);
        }
    }, [allRussianRouletteFiles]);

    // Mutations
    const acceptTranslationRussianRouletteMutation = useMutation({
        mutationFn: async (translationId: number) => {
            const { token, param } = getCsrfToken();
            const response = await fetch(`/api/extracted_texts/${translationId}/accept_translation`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    [param || 'X-CSRF-Token']: token || '',
                },
                body: JSON.stringify({}),
            });
            if (!response.ok) throw new Error('Failed to accept translation');
            return response.json();
        },
        onMutate: (translationId) => {
            setAcceptingIds(prev => new Set([...prev, translationId]));
        },
        onSuccess: (_data, translationId) => {
            setAcceptedIds(prev => new Set([...prev, translationId]));
            setAcceptingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(translationId);
                return newSet;
            });
        },
        onError: (error) => {
            toast({ title: "Hiba az elfogadásnál", description: error.message, variant: "destructive" });
        },
        onSettled: (_data, _error, translationId) => {
            setAcceptingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(translationId);
                return newSet;
            });
        },
    });

    const revertAcceptanceMutation = useMutation({
        mutationFn: async (translationId: number) => {
            const { token, param } = getCsrfToken();
            const response = await fetch(`/api/extracted_texts/${translationId}/revert_acceptance`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    [param || 'X-CSRF-Token']: token || '',
                },
            });
            if (!response.ok) throw new Error('Failed to revert acceptance');
            return response.json();
        },
        onSuccess: (_data, translationId) => {
            setAcceptedIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(translationId);
                return newSet;
            });
            toast({ title: "Elfogadás visszavonva!" });
        },
    });

    const deleteTranslationMutation = useMutation({
        mutationFn: async (translationId: number) => {
            const { token, param } = getCsrfToken();
            const response = await fetch(`/api/extracted_texts/${translationId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    [param || 'X-CSRF-Token']: token || '',
                },
            });
            if (!response.ok) throw new Error('Failed to delete translation');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["russianRoulette", gameId, versionId, russianRoulettePage, appliedRussianRouletteFileFilter] });
            toast({ title: "Fordítás törölve!" });
        },
    });



    const geminiCheckAllMutation = useMutation({
        mutationFn: async ({ translations }: { translations: { id: number; source_text: string; target_text: string }[] }) => {
            const { token } = getCsrfToken();
            if (!token) {
                throw new Error("CSRF token nem található.");
            }

            const response = await fetch(`/api/gemini/check_all_translations`, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ translations: translations }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Gemini API hívás sikertelen");
            }
            return response.json();
        },
        onSuccess: (data) => {
            const newResults = new Map<number, string>();
            for (const id in data.results) {
                newResults.set(parseInt(id, 10), data.results[id]);
            }
            setGeminiResults(prev => new Map([...prev, ...newResults]));
            toast({ title: "Gemini tömeges ellenőrzés sikeres!" });
        },
        onError: (error) => {
            toast({ title: "Gemini tömeges ellenőrzés sikertelen!", description: error.message, variant: "destructive" });
        },
    });

    if (isLoadingRussianRoulette) {
        return <p>Javaslatok betöltése...</p>;
    }

    return (
        <div className="container mx-auto p-4 space-y-8 bg-background text-foreground">
            <Breadcrumb className="mb-4">
                <BreadcrumbList>
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to="/games">Játékok</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/games/${gameId}`}>Játék</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/games/${gameId}/versions/${versionId}`}>Verzió: {versionId}</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>Orosz Rulett</BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>
            <VersionNavigation gameId={gameId} versionId={versionId} />
            <Card>
                <CardHeader>
                    <CardTitle>Orosz Rulett</CardTitle>
                    <CardDescription>
                        Itt azonos tartalmú, de különböző helyen lévő szövegek javaslatait látod. Fogadd el vagy töröld a javaslatokat.
                    </CardDescription>
                    {allRussianRouletteFiles.length > 0 && (
                        <div className="flex flex-col gap-2 mt-4">
                            <div className="font-semibold text-sm">Szűrés:</div>
                            <div className="flex flex-wrap gap-2">
                                <label className="flex items-center gap-1 border rounded px-2 py-1 bg-muted cursor-pointer">
                                    <Checkbox
                                        checked={appliedRussianRouletteFileFilter.length === allRussianRouletteFiles.length}
                                        onCheckedChange={(checked) => {
                                            if (checked) {
                                                setAppliedRussianRouletteFileFilter([...allRussianRouletteFiles]);
                                                setPendingRussianRouletteFileFilter([...allRussianRouletteFiles]);
                                            } else {
                                                setAppliedRussianRouletteFileFilter([]);
                                                setPendingRussianRouletteFileFilter([]);
                                            }
                                        }}
                                        id="select-all"
                                    />
                                    <span className="text-xs font-mono">Összes</span>
                                </label>
                                {allRussianRouletteFiles.map((filename) => (
                                    <label key={filename} className="flex items-center gap-1 border rounded px-2 py-1 bg-muted cursor-pointer">
                                        <Checkbox
                                            checked={pendingRussianRouletteFileFilter.includes(filename)}
                                            onCheckedChange={(checked) => {
                                                setPendingRussianRouletteFileFilter((prev: string[]) => {
                                                    const newPending = checked
                                                        ? [...prev, filename]
                                                        : prev.filter((f) => f !== filename);
                                                    if (newPending.length === allRussianRouletteFiles.length) {
                                                        setAppliedRussianRouletteFileFilter([...allRussianRouletteFiles]);
                                                    } else {
                                                        setAppliedRussianRouletteFileFilter([]);
                                                    }
                                                    return newPending;
                                                });
                                            }}
                                        />
                                        <span className="text-xs font-mono truncate max-w-xs" title={filename}>{filename}</span>
                                    </label>
                                ))}
                            </div>
                            <div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="mt-1"
                                    onClick={() => {
                                        setAppliedRussianRouletteFileFilter([...pendingRussianRouletteFileFilter]);
                                    }}
                                    disabled={pendingRussianRouletteFileFilter.length === 0}
                                >
                                    Szűrés
                                </Button>
                            </div>
                        </div>
                    )}
                    <div className="flex flex-col items-end mt-2">
                        {user?.admin && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    if (russianRouletteData?.suggestions) {
                                        const translations = russianRouletteData.suggestions.map((s: any) => ({
                                            id: s.id,
                                            source_text: s.parent_text?.content || '',
                                            target_text: s.content || '',
                                        }));
                                        geminiCheckAllMutation.mutate({ translations });
                                    }
                                }}
                                disabled={geminiCheckAllMutation.isPending || !russianRouletteData?.suggestions || russianRouletteData.suggestions.length === 0}
                            >
                                {geminiCheckAllMutation.isPending ? 'Minden ellenőrzése...' : 'Mindet ellenőrizd'}
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    {russianRouletteData && russianRouletteData.suggestions.length > 0 ? (
                        <>
                            <div className="flex items-center justify-between mb-4">
                                <Pagination>
                                    <PaginationContent>
                                        <PaginationItem>
                                            <PaginationPrevious
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setRussianRoulettePage(p => Math.max(1, p - 1));
                                                }}
                                                className={russianRouletteData.current_page === 1 ? 'pointer-events-none opacity-50' : ''}
                                            />
                                        </PaginationItem>
                                        {generatePaginationPages(russianRouletteData.current_page, russianRouletteData.total_pages).map((page, index) => (
                                            <PaginationItem key={index}>
                                                {page === "..." ? (
                                                    <PaginationEllipsis />
                                                ) : (
                                                    <PaginationLink
                                                        href="#"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setRussianRoulettePage(page as number);
                                                        }}
                                                        isActive={page === russianRouletteData.current_page}
                                                    >
                                                        {page}
                                                    </PaginationLink>
                                                )}
                                            </PaginationItem>
                                        ))}
                                        <PaginationItem>
                                            <PaginationNext
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setRussianRoulettePage(p => Math.min(russianRouletteData.total_pages, p + 1));
                                                }}
                                                className={russianRouletteData.current_page === russianRouletteData.total_pages ? 'pointer-events-none opacity-50' : ''}
                                            />
                                        </PaginationItem>
                                    </PaginationContent>
                                </Pagination>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">Ugrás:</span>
                                    <Input
                                        type="number"
                                        min={1}
                                        max={russianRouletteData.total_pages}
                                        className="w-20"
                                        placeholder="Oldal"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                const value = parseInt((e.target as HTMLInputElement).value);
                                                if (value >= 1 && value <= russianRouletteData.total_pages) {
                                                    setRussianRoulettePage(value);
                                                    (e.target as HTMLInputElement).value = '';
                                                }
                                            }
                                        }}
                                    />
                                    <span className="text-sm text-muted-foreground">/ {russianRouletteData.total_pages}</span>
                                </div>
                            </div>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-5/12">Angol (Eredeti)</TableHead>
                                        <TableHead className="w-5/12">Magyar (Javaslat)</TableHead>
                                        <TableHead className="w-2/12 text-right">Műveletek</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {russianRouletteData.suggestions
                                        .map((suggestion: any) => [
                                            <TableRow key={suggestion.id} className={suggestion.status === 'accepted' || acceptedIds.has(suggestion.id) || acceptingIds.has(suggestion.id) ? 'bg-green-100 dark:bg-green-900' : ''}>
                                                <TableCell className="font-medium w-5/12">{suggestion.parent_text?.content}</TableCell>
                                                <TableCell className="w-5/12">
                                                    {suggestion.content}
                                                </TableCell>
                                                <TableCell className="text-right w-2/12">
                                                    {(user?.admin || user?.translater_admin) && suggestion.status !== 'accepted' && !acceptedIds.has(suggestion.id) && (
                                                        <>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="p-1 h-auto transition-all duration-200 hover:text-green-500"
                                                                onClick={() => acceptTranslationRussianRouletteMutation.mutate(suggestion.id)}
                                                                disabled={acceptingIds.has(suggestion.id) || acceptTranslationRussianRouletteMutation.isPending}
                                                                title="Elfogadás"
                                                            >
                                                                <CheckCircle className="w-5 h-5" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="p-1 h-auto transition-all duration-200 hover:text-red-500"
                                                                onClick={() => {
                                                                    if (window.confirm("Biztosan törölni szeretnéd ezt a javaslatot?")) {
                                                                        deleteTranslationMutation.mutate(suggestion.id);
                                                                    }
                                                                }}
                                                                disabled={deleteTranslationMutation.isPending}
                                                                title="Törlés"
                                                            >
                                                                <Trash2 className="w-5 h-5" />
                                                            </Button>
                                                            {geminiResults.has(suggestion.id) && (
                                                                <div className="text-xs text-left whitespace-pre-wrap mt-2 bg-gray-50 dark:bg-gray-800 p-2 rounded">
                                                                    {geminiResults.get(suggestion.id)}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                    {(user?.admin || user?.translater_admin) && (suggestion.status === 'accepted' || acceptedIds.has(suggestion.id)) && (
                                                        <>
                                                            <Button
                                                                onClick={() => {
                                                                    if (window.confirm("Biztosan vissza szeretnéd vonni ezt az elfogadást?")) {
                                                                        revertAcceptanceMutation.mutate(suggestion.id);
                                                                    }
                                                                }}
                                                                disabled={revertAcceptanceMutation.isPending}
                                                                size="sm"
                                                                variant="ghost"
                                                                className="p-1 h-auto transition-all duration-200 hover:text-yellow-500"
                                                                title="Visszavonás"
                                                            >
                                                                <RotateCcw className="w-5 h-5" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="p-1 h-auto transition-all duration-200 hover:text-red-500"
                                                                onClick={() => {
                                                                    if (window.confirm("Biztosan törölni szeretnéd ezt a javaslatot?")) {
                                                                        deleteTranslationMutation.mutate(suggestion.id);
                                                                    }
                                                                }}
                                                                disabled={deleteTranslationMutation.isPending}
                                                                title="Törlés"
                                                            >
                                                                <Trash2 className="w-5 h-5" />
                                                            </Button>
                                                            {geminiResults.has(suggestion.id) && (
                                                                <div className="text-xs text-left whitespace-pre-wrap mt-2 bg-gray-50 dark:bg-gray-800 p-2 rounded">
                                                                    {geminiResults.get(suggestion.id)}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </TableCell>
                                            </TableRow>,
                                            <TableRow key={suggestion.id + '-info'}>
                                                <TableCell colSpan={4} className="bg-muted text-xs text-gray-700 dark:text-gray-300 p-2">
                                                    <div className="flex flex-wrap gap-4 items-center">
                                                        <div><span className="font-semibold">Verzió:</span> {suggestion.parent_text?.version_name || '-'}</div>
                                                        <div><span className="font-semibold">Fájlnév:</span> {suggestion.parent_text?.filename || '-'}</div>
                                                        <div className="flex items-center gap-1">
                                                            <span className="font-semibold">Tényleges fájlnév:</span> {suggestion.parent_text?.name || '-'}
                                                            {suggestion.parent_text?.id && (
                                                                <a
                                                                    href={`/games/${gameId}/versions/${versionId}/translate?highlight_id=${suggestion.parent_text.id}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="h-6 w-6 p-0 inline-flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 rounded flex-shrink-0"
                                                                    title="Ugrás az ismétlődésekhez új ablakban"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                    </svg>
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ])}
                                </TableBody>
                            </Table>
                            {/* Lapozó a lista alján is */}
                            <div className="flex items-center justify-between mt-4">
                                <Pagination>
                                    <PaginationContent>
                                        <PaginationItem>
                                            <PaginationPrevious
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setRussianRoulettePage(p => Math.max(1, p - 1));
                                                }}
                                                className={russianRouletteData.current_page === 1 ? 'pointer-events-none opacity-50' : ''}
                                            />
                                        </PaginationItem>
                                        {generatePaginationPages(russianRouletteData.current_page, russianRouletteData.total_pages).map((page, index) => (
                                            <PaginationItem key={index}>
                                                {page === "..." ? (
                                                    <PaginationEllipsis />
                                                ) : (
                                                    <PaginationLink
                                                        href="#"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setRussianRoulettePage(page as number);
                                                        }}
                                                        isActive={page === russianRouletteData.current_page}
                                                    >
                                                        {page}
                                                    </PaginationLink>
                                                )}
                                            </PaginationItem>
                                        ))}
                                        <PaginationItem>
                                            <PaginationNext
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setRussianRoulettePage(p => Math.min(russianRouletteData.total_pages, p + 1));
                                                }}
                                                className={russianRouletteData.current_page === russianRouletteData.total_pages ? 'pointer-events-none opacity-50' : ''}
                                            />
                                        </PaginationItem>
                                    </PaginationContent>
                                </Pagination>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">Ugrás:</span>
                                    <Input
                                        type="number"
                                        min={1}
                                        max={russianRouletteData.total_pages}
                                        className="w-20"
                                        placeholder="Oldal"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                const value = parseInt((e.target as HTMLInputElement).value);
                                                if (value >= 1 && value <= russianRouletteData.total_pages) {
                                                    setRussianRoulettePage(value);
                                                    (e.target as HTMLInputElement).value = '';
                                                }
                                            }
                                        }}
                                    />
                                    <span className="text-sm text-muted-foreground">/ {russianRouletteData.total_pages}</span>
                                </div>
                            </div>
                        </>
                    ) : (
                        <p>Nincsenek ismétlődő szövegek javaslatokkal ebben a verzióban.</p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

// Wrapper component for standalone page
const Gomb3PageWrapper = () => {
    const { user } = useUser();
    return <Gomb3Page user={user} />;
};

export default Gomb3PageWrapper;
